import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Paper, Button, List, ListItem, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { generateSitemap, getStaticPages, generateProductUrls, downloadSitemap, downloadRobotsTxt, type SitemapUrl } from '../utils/sitemapGenerator';

const Sitemap: React.FC = () => {
  const [sitemapContent, setSitemapContent] = useState<string>('');
  const [urls, setUrls] = useState<SitemapUrl[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateSitemapData = async () => {
      setLoading(true);
      try {
        const staticPages = getStaticPages();
        const productPages = await generateProductUrls();
        const allPages = [...staticPages, ...productPages];
        
        setUrls(allPages);
        setSitemapContent(generateSitemap(allPages));
      } catch (error) {
        console.error('Error generating sitemap:', error);
      } finally {
        setLoading(false);
      }
    };

    generateSitemapData();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading sitemap...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Sitemap
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4 }}>
        This page shows all the URLs on our website. You can also download the XML sitemap file.
      </Typography>

      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={downloadSitemap}
          sx={{ mr: 2 }}
        >
          Download XML Sitemap
        </Button>
        <Button 
          variant="outlined" 
          onClick={downloadRobotsTxt}
        >
          Download robots.txt
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Site Pages ({urls.length} total)
        </Typography>
        
        <List>
          {urls.map((url, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <MuiLink 
                component={RouterLink} 
                to={url.loc}
                sx={{ textDecoration: 'none' }}
              >
                {url.loc === '/' ? 'Home' : url.loc}
              </MuiLink>
              {url.priority && (
                <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                  Priority: {url.priority}
                </Typography>
              )}
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          XML Sitemap Content
        </Typography>
        <Box 
          component="pre" 
          sx={{ 
            overflow: 'auto', 
            backgroundColor: '#f5f5f5', 
            p: 2, 
            borderRadius: 1,
            fontSize: '0.875rem',
            maxHeight: '400px'
          }}
        >
          {sitemapContent}
        </Box>
      </Paper>
    </Container>
  );
};

export default Sitemap;
